/**
 * 사용자 및 방문자 접근 관리 컴포넌트
 * 사용자와 방문자의 접근 권한을 관리하는 컴포넌트입니다.
 * 사용자 관리와 방문자 관리 섹션을 포함합니다.
 */

import _ from "lodash";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { AcGroupContractManageModel } from "src/api/access/ac-group-types";
import { getContractOperationInfo } from "src/api/contract/contract-api";
import { ContractOperationProductManage } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { checkDataNMake } from "src/pages/contract/hooks/useContractApi";
import useContractFacilityApi from "src/pages/contract/hooks/useContractFacilityApi";
import { PagePath } from "src/pages/product/details";
import { useModal } from "src/recoil/modalState/hook";
import { useToast } from "src/recoil/toast/hook";
import UserAccessSection from "./UserAccessSection";
import VisitorAccessSection from "./VisitorAccessSection";
import { useS2Contract } from "src/pages/contract/hooks/useS2Contract";

/**
 * 사용자 및 방문자 접근 관리 컴포넌트의 props 인터페이스
 */
type Props = {
  contractApplyNumber: string; // 계약 신청 번호
  contractManage: AcGroupContractManageModel | undefined; // 계약 관리 정보
  onChangeVisitorApprove: (visitorDefaultAccessGroupYn: boolean) => Promise<void>; // 방문자 승인 설정 변경 핸들러
};

/**
 * 사용자 및 방문자 접근 관리 컴포넌트
 * @param contractApplyNumber 계약 신청 번호
 * @param contractManage 계약 관리 정보
 * @param onChangeVisitorApprove 방문자 승인 설정 변경 핸들러
 */
const UserAndVisitorAccess = ({ contractApplyNumber, contractManage, onChangeVisitorApprove }: Props) => {
  const { id } = useParams();
  const location = useLocation();
  const { isS2Contract } = useS2Contract();

  // URL 쿼리 파라미터 파싱
  const queryParams = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        allowDots: true,
      }),
    [location],
  );

  const { openToast } = useToast();
  const { isAuthority } = usePartnerAuthority();
  const { setBaseModal } = useModal();

  const [productManage, setProductManage] = useState<ContractOperationProductManage>(); // 상품 관리 정보
  const [originalProductManage, setOriginalProductManage] = useState<ContractOperationProductManage>(); // 원본 상품 관리 정보

  /** RCA31. 계약 - 운영정보 상세 조회 */
  const { executeAsync: getContractOperation } = useApiOperation(getContractOperationInfo);

  /** RCA33. 계약 - 운영정보 수정 */
  const { setContractOperation } = useContractFacilityApi();

  // 계약 ID 설정
  const contractId = useMemo(() => {
    return queryParams?.id ? String(queryParams.id) : id;
  }, [id, queryParams.id]);

  /**
   * 운영정보 상세 조회 함수
   * 최대인원 멤버 및 현재 이용자 수 확인
   * @param id 계약 ID
   */
  const fetchContractOperation = async (id: string) => {
    const response = await getContractOperation({ id: Number(id) });
    const result = response.data.data.content.productManage;
    setProductManage(result);
    setOriginalProductManage(_.cloneDeep(result));
  };

  // 계약 ID가 변경될 때 운영정보를 조회
  useEffect(() => {
    if (contractId) {
      fetchContractOperation(contractId);
    }
  }, []);

  // 사용자 목록 페이지 URL
  const moveUserList = useMemo(() => {
    return PagePath.user.list + "?contractApplyNumber=" + contractApplyNumber + "&contractId=" + contractId;
  }, [contractApplyNumber, contractId]);

  // 방문자 목록 페이지 URL
  const moveVisitorList = useMemo(() => {
    return PagePath.visitor.list + "?contractApplyNumber=" + contractApplyNumber + "&contractId=" + contractId;
  }, [contractApplyNumber, contractId]);

  /**
   * 상품 관리 정보 수정 함수
   * 이용자 관리 - 최대 이용자수 변경
   * @param data 수정할 상품 관리 정보
   */
  const editProductManageData = async (data: ContractOperationProductManage) => {
    if (data) {
      let rtnValidation = { validationPass: false, message: "", response: {} };
      let rtnData: any = {};
      let response: any = checkDataNMake(rtnValidation, originalProductManage, data, rtnData);

      if (response.rtnData.validationPass) {
        await setContractOperation({ id: Number(contractId), productManage: response.makeData });
        fetchContractOperation(String(contractId));
      }
    }
  };

  /**
   * 방문자 변경 이벤트 처리 함수
   * @param requestFn 실행할 함수
   * @param type 변경 유형 (invite: 초대, approval: 승인)
   */
  const handleVisitorChangeEvent = (requestFn: Function, type?: "invite" | "approval") => {
    setBaseModal({
      isOpen: true,
      title: "저장하시겠습니까?",
      // children: type === "invite" ? "[Taap > 방문자 초대] 사용 가능 여부가 즉시 적용됩니다." : "이미 초대한 방문자의 권한에도 적용됩니다.",
      onClick: async () => {
        await requestFn();
        setBaseModal({ isOpen: false });
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
    });
  };

  return (
    <>
      {/* 이용자 관리 섹션 */}
      {!isS2Contract && (
        <article className="contents-container__wrap-article">
          <UserAccessSection
            productManage={productManage || {}}
            goToList={moveUserList}
            setData={(data: ContractOperationProductManage) => {
              if (data) {
                editProductManageData(data);
              }
            }}
            isAuthority={isAuthority("w")}
          />
        </article>
      )}

      {/* 방문자 관리 섹션 */}
      <article className="contents-container__wrap-article">
        <VisitorAccessSection
          productManage={productManage || {}}
          contractManage={contractManage}
          onChangeVisitor={async (data: { productManage?: ContractOperationProductManage; visitorDefaultAccessGroupYn?: boolean }) => {
            return new Promise<void>((resolve) => {
              handleVisitorChangeEvent(async () => {
                // productManage 변경사항 처리
                if (data.productManage) {
                  await editProductManageData(data.productManage);
                }
                // visitorDefaultAccessGroupYn 변경사항 처리
                if (data.visitorDefaultAccessGroupYn !== undefined) {
                  await onChangeVisitorApprove(data.visitorDefaultAccessGroupYn);
                }
                resolve();
              });
            });
          }}
          isAuthority={isAuthority("w")}
          visitorUrl={moveVisitorList}
        />
      </article>
    </>
  );
};

export default UserAndVisitorAccess;
